// ============================================================================
// FSA NOTE — SHARED ROUNDING HELPERS
// ============================================================================
// Mirrors EngagementNoteDataService.AutoRound on the backend so frontend
// can preview rounded values immediately, before next save.
//
// Divisor per factor key:
//   THOUSANDS → 1,000        e.g. 5,000,000 → 5,000.00
//   LAKHS     → 100,000      e.g. 5,000,000 → 50.00
//   CRORES    → 10,000,000   e.g. 50,000,000 → 5.00
//   MILLIONS  → 1,000,000    e.g. 5,000,000 → 5.00
//   BILLIONS  → 1,000,000,000
//   ACTUALS / unknown → 1 (no divisor)
//
// All rounding is half-up to 2 decimal places (matches backend).

export type RoundingFactor =
  | 'ACTUALS'
  | 'THOUSANDS'
  | 'LAKHS'
  | 'CRORES'
  | 'MILLIONS'
  | 'BILLIONS'
  | string;

/** Default tolerance applied to disclosure amount cells when an auditor
 *  override drifts from the auto-rounded value. Mirrors
 *  fsa.tbl_fsa_engagement_settings.rounding_tolerance default. */
export const DEFAULT_ROUNDING_TOLERANCE = 1.0;

// Rounding-factor divisors are DYNAMIC — sourced from the rounding-factor
// enum's meta_data.divisor and delivered on the note hydrate
// (hydrate.roundingFactorDivisor), then registered via registerRoundingDivisor().
// Adding a new factor (e.g. HUNDREDS → 100) is a pure enum/data change — no edit
// needed here. The seeded values are only a safety fallback for the known legacy
// factors; the engagement's actual divisor always overrides them once registered.
const ROUNDING_DIVISORS: Record<string, number> = {
  THOUSANDS: 1_000,
  LAKHS: 100_000,
  CRORES: 10_000_000,
  MILLIONS: 1_000_000,
  BILLIONS: 1_000_000_000,
};

/** Register an engagement's rounding factor → divisor (from the hydrate's enum-resolved value). */
export function registerRoundingDivisor(
  factor: string | null | undefined,
  divisor: number | null | undefined,
): void {
  if (factor && typeof divisor === 'number' && divisor > 0) {
    ROUNDING_DIVISORS[factor] = divisor;
  }
}

export function autoRound(actual: number, factor: RoundingFactor | null | undefined): number {
  const divisor = (factor && ROUNDING_DIVISORS[factor]) || 1;
  return Math.round((actual / divisor) * 100) / 100;
}

/** Tolerance preserver — same shape the backend uses on save. Returns the
 *  user's prior rounded override when it's within tolerance of the freshly
 *  auto-rounded value; otherwise snaps to the auto. */
export function applyRoundingTolerance(
  storedRounded: number,
  newAuto: number,
  tolerance: number = DEFAULT_ROUNDING_TOLERANCE,
): number {
  if (storedRounded === 0 && newAuto !== 0) return newAuto;
  if (Math.abs(newAuto - storedRounded) <= tolerance) return storedRounded;
  return newAuto;
}

/** JSON key suffix for the rounded companion to an amount cell. The
 *  canonical actual value lives at row[col_code]; the rounded value lives
 *  at row[col_code + ROUNDED_SUFFIX]. Computed cells write both. */
export const ROUNDED_SUFFIX = '__rounded';

/** Format a rounding factor key into a human-readable units phrase
 *  (e.g. "Rupees Lakhs"). Used in section-header hints. */
export function factorUnitsLabel(factor: RoundingFactor | null | undefined): string {
  switch (factor) {
    case 'THOUSANDS': return 'Rupees Thousands';
    case 'LAKHS':     return 'Rupees Lakhs';
    case 'CRORES':    return 'Rupees Crores';
    case 'MILLIONS':  return 'Rupees Millions';
    case 'BILLIONS':  return 'Rupees Billions';
    default:          return 'Rupees';
  }
}
